import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSnackbar } from 'notistack';
import supportResourcesApi from '../api/supportResources';
import { formatDate } from '../utils/format';

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'Students & Tutors' },
  { value: 'student', label: 'Students only' },
  { value: 'educator', label: 'Tutors only' },
];

const SupportResourcesPanel = ({ allowUpload = false, userRole = 'student' }) => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    audience: 'all',
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const loadResources = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await supportResourcesApi.list();
      setResources(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
    if (event.target) {
      // allow re-selecting the same files later
      event.target.value = '';
    }
  };

  const resetForm = () => {
    setForm({ title: '', description: '', audience: 'all' });
    setSelectedFiles([]);
  };

  const submitUpload = async (event) => {
    event.preventDefault();
    if (!selectedFiles.length) {
      enqueueSnackbar('Attach at least one file', { variant: 'warning' });
      return;
    }
    setUploading(true);
    try {
      const payload = new FormData();
      payload.append('title', form.title);
      payload.append('description', form.description);
      payload.append('audience', form.audience);
      selectedFiles.forEach((file) => {
        payload.append('files', file);
      });
      await supportResourcesApi.create(payload);
      enqueueSnackbar('Resources uploaded', { variant: 'success' });
      resetForm();
      loadResources();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to upload files', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (resourceId) => {
    if (!window.confirm('Remove this resource for everyone?')) return;
    try {
      await supportResourcesApi.remove(resourceId);
      enqueueSnackbar('Resource removed', { variant: 'info' });
      loadResources();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to remove resource', { variant: 'error' });
    }
  };

  const helpfulCopy = useMemo(() => {
    if (userRole === 'educator') {
      return 'Save time with ready-to-download guides and policy documents curated by Apex support.';
    }
    if (userRole === 'admin') {
      return 'Share common troubleshooting guides or onboarding packs so students and tutors can self-serve answers.';
    }
    return 'Browse quick reference guides and onboarding documents shared by the support team.';
  }, [userRole]);

  return (
    <Card elevation={4} sx={{ borderRadius: 3 }}>
      <CardHeader
        avatar={<InfoOutlinedIcon color="primary" />}
        title={<Typography variant="h6">Helpful downloads</Typography>}
        subheader={helpfulCopy}
        subheaderTypographyProps={{ sx: { color: 'text.secondary' } }}
      />
      <Divider />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {allowUpload && (
          <Box component="form" onSubmit={submitUpload} sx={{ mb: 4 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Upload new files
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Title"
                  name="title"
                  value={form.title}
                  onChange={handleFieldChange}
                  placeholder="e.g. Getting started guide"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Audience"
                  name="audience"
                  value={form.audience}
                  onChange={handleFieldChange}
                  fullWidth
                >
                  {AUDIENCE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  name="description"
                  value={form.description}
                  onChange={handleFieldChange}
                  placeholder="Short summary or instructions"
                  multiline
                  minRows={2}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                  >
                    {selectedFiles.length ? `${selectedFiles.length} file(s) selected` : 'Choose files'}
                    <input type="file" hidden multiple onChange={handleFileChange} />
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </Button>
                  {selectedFiles.length > 0 && (
                    <Button color="inherit" onClick={() => setSelectedFiles([])}>
                      Clear selected
                    </Button>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Accepts PDF, Office files, images, and text documents up to 25MB each.
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={{ my: 3 }} />
          </Box>
        )}

        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading resources...
          </Typography>
        ) : resources.length === 0 ? (
          <Alert severity="info" variant="outlined">
            No downloads are available yet. {allowUpload ? 'Upload a file to get started.' : 'Check back soon.'}
          </Alert>
        ) : (
          <Stack spacing={2}>
            {resources.map((resource) => {
              const audienceOption = AUDIENCE_OPTIONS.find((opt) => opt.value === resource.audience) || AUDIENCE_OPTIONS[0];
              return (
                <Box
                  key={resource._id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {resource.title}
                      </Typography>
                      {resource.description && (
                        <Typography variant="body2" color="text.secondary">
                          {resource.description}
                        </Typography>
                      )}
                    </Box>
                    {allowUpload && (
                      <IconButton
                        color="error"
                        aria-label="Delete resource"
                        onClick={() => handleDelete(resource._id)}
                      >
                        <DeleteForeverIcon />
                      </IconButton>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ my: 1 }} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={audienceOption.label} />
                    <Chip size="small" label={`Added ${formatDate(resource.createdAt)}`} />
                    {resource.createdByName && (
                      <Chip size="small" label={`by ${resource.createdByName}`} />
                    )}
                  </Stack>
                  <Stack spacing={1}>
                    {(resource.files || []).map((file) => (
                      <Button
                        key={`${resource._id}-${file.key}`}
                        variant="outlined"
                        startIcon={<FileDownloadOutlinedIcon />}
                        component="a"
                        href={file.url}
                        target="_blank"
                        rel="noopener"
                      >
                        {file.originalName}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default SupportResourcesPanel;
